import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, MapPin, Ticket, CalendarPlus } from 'lucide-react';
import Reveal from './Reveal';
import { tourDates as mockTourDates } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  const d = new Date(iso);
  return {
    day: d.toLocaleDateString('en-GB', { day: '2-digit' }),
    month: d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
    year: d.getFullYear(),
    weekday: d.toLocaleDateString('en-GB', { weekday: 'long' }),
  };
};

const pad = (n) => String(n).padStart(2, '0');

const downloadICS = (show) => {
  const d = new Date(show.date);
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const dtStart = `${stamp}T193000`;
  const dtEnd = `${stamp}T223000`;
  const uid = `${show.id || stamp}@fleetwoodmacexperience`;
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//The Fleetwood Mac Experience//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}T000000Z`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:The Fleetwood Mac Experience — ${show.venue}`,
    `LOCATION:${show.venue}, ${show.city}, ${show.country}`,
    'DESCRIPTION:A joyful live theatre show celebrating the timeless music of Fleetwood Mac.',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fleetwood-mac-experience-${show.city
    .toLowerCase()
    .replace(/\s+/g, '-')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const TicketAction = ({ soldOut, ticketUrl }) => {
  if (soldOut) {
    return (
      <span className="inline-block bg-[#1a1a1a] text-white px-6 py-2.5 rounded-full text-[12px] tracking-[0.2em] font-semibold">
        SOLD OUT
      </span>
    );
  }
  if (ticketUrl && ticketUrl !== '#') {
    return (
      <a
        href={ticketUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-[#e6396b] hover:bg-[#cf2a5b] text-white px-6 py-2.5 rounded-full text-[12px] tracking-[0.2em] font-semibold transition-colors"
      >
        <Ticket size={14} />
        TICKETS
      </a>
    );
  }
  return null;
};

const DateCard = ({ show }) => {
  const d = formatDate(show.date);
  const soldOut = show.sold_out ?? show.soldOut;
  const ticketUrl = show.ticket_url ?? show.ticketUrl ?? '#';

  return (
    <div className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-[#e7ddd0] flex flex-col md:flex-row items-stretch overflow-hidden hover:border-[#e6396b]/40">
      <div className="bg-gradient-to-br from-[#e6396b] to-[#cf2a5b] text-white px-6 py-5 md:py-4 md:w-32 flex md:flex-col items-center justify-center gap-2 md:gap-1.5">
        <span className="font-serif text-[38px] md:text-[44px] leading-none font-medium">
          {d.day}
        </span>
        <span className="text-[12px] tracking-[0.25em] font-semibold">
          {d.month}
        </span>
        <span className="text-[11px] opacity-80 hidden md:block">{d.year}</span>
      </div>

      <div className="flex-1 px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-[12px] text-[#e6396b] font-semibold tracking-[0.2em] mb-1.5">
            <Calendar size={12} className="inline mr-1.5 -mt-0.5" />
            {d.weekday.toUpperCase()}
          </p>
          <h3 className="font-serif text-[22px] md:text-[26px] text-[#1a1a1a] font-medium leading-tight">
            {show.venue}
          </h3>
          <p className="text-[14px] text-[#6a6a6a] mt-1.5 flex items-center gap-1.5">
            <MapPin size={14} className="text-[#e6396b]" />
            {show.city}, {show.country}
          </p>
        </div>

        <div className="md:ml-auto flex items-center gap-2">
          <button
            onClick={() => downloadICS(show)}
            aria-label="Add to calendar"
            title="Add to calendar"
            className="w-10 h-10 rounded-full border border-[#e7ddd0] text-[#6a6a6a] hover:border-[#e6396b] hover:text-[#e6396b] flex items-center justify-center transition-colors"
          >
            <CalendarPlus size={16} />
          </button>
          <TicketAction soldOut={soldOut} ticketUrl={ticketUrl} />
        </div>
      </div>
    </div>
  );
};

const Dates = () => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDates = useCallback(async (signal) => {
    try {
      const res = await axios.get(`${API}/tour-dates`, { signal });
      if (Array.isArray(res.data) && res.data.length) {
        return res.data;
      }
      return mockTourDates;
    } catch {
      return mockTourDates;
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    (async () => {
      const result = await fetchDates(controller.signal);
      if (active) {
        setDates(result);
        setLoading(false);
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [fetchDates]);

  return (
    <section id="dates" className="py-24 md:py-32 bg-[#efe6d8]/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="text-center mb-14">
          <p className="text-[#e6396b] tracking-[0.4em] text-[12px] font-semibold mb-5">
            LIVE
          </p>
          <h2 className="font-serif text-[44px] md:text-[64px] text-[#1a1a1a] font-medium">
            Upcoming Shows
          </h2>
          <p className="mt-4 text-[16px] text-[#6a6a6a]">
            Catch us live — secure your tickets before they're gone.
          </p>
        </Reveal>

        <div className="max-w-4xl mx-auto space-y-4">
          {loading && (
            <div className="text-center text-[#6a6a6a] py-10">
              Loading tour dates…
            </div>
          )}
          {!loading &&
            dates.map((show, i) => (
              <Reveal key={show.id || `${show.date}-${show.venue}`} delay={i * 70}>
                <DateCard show={show} />
              </Reveal>
            ))}
        </div>
      </div>
    </section>
  );
};

export default Dates;
